import { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import type { CardView, Stage } from "@breeze/shared";
import { STAGES } from "@breeze/shared";
import { Column } from "./components/Column.js";
import { Card } from "./components/Card.js";
import { LostReasonModal } from "./components/LostReasonModal.js";
import { NewQuoteSearchModal } from "./components/NewQuoteSearchModal.js";
import { QuoteEditor } from "./components/QuoteEditor.js";
import { Slideout } from "./components/Slideout.js";
import { AccountSlideout } from "./components/AccountSlideout.js";
import { Toolbar } from "./components/Toolbar.js";
import { Login } from "./components/Login.js";
import { BulkBar } from "./components/BulkBar.js";
import { TopNav } from "./components/TopNav.js";
import { CleanerEarnings } from "./components/CleanerEarnings.js";
import { BusinessGrowth } from "./components/BusinessGrowth.js";
import { Marketing } from "./components/Marketing.js";
import { TechCoverage } from "./components/TechCoverage.js";
import { AreaAnalysis } from "./components/AreaAnalysis.js";
import { DbBoard } from "./components/DbBoard.js";
import { useBoard, usePatchCard } from "./hooks/useBoard.js";
import { useRealtime } from "./hooks/useRealtime.js";
import { useSession } from "./hooks/useSession.js";
import {
  useCurrentUserStore,
  useFilters,
  type SortMode,
} from "./store/filters.js";
import { useView } from "./store/view.js";

const POSITION_GAP = 1000;

type PendingMove = {
  cardId: number;
  stage: Stage;
  position: number;
};

function compareCards(a: CardView, b: CardView, sort: SortMode): number {
  switch (sort) {
    case "newest":
      return cmpDateDesc(a.lastActivityAt, b.lastActivityAt);
    case "oldest":
      return cmpDateAsc(a.lastActivityAt, b.lastActivityAt);
    case "priority":
      if (a.priority !== b.priority) return b.priority - a.priority;
      return a.position - b.position;
    case "value":
      return (b.totalCost ?? 0) - (a.totalCost ?? 0);
    case "manual":
    default:
      return a.position - b.position;
  }
}

// Mirrors Card.tsx + Slideout.tsx flash logic — kept here so the WON
// column sort can float flashing cards to the top. Any change in the
// flash triggers needs to be updated in all three places.
function needsAttention(c: CardView): boolean {
  const lastReadMs = c.lastReadAt ? new Date(c.lastReadAt).getTime() : 0;
  const hasUnreadInbound =
    c.lastMessageAt !== null &&
    c.lastMessageDirection === "in" &&
    new Date(c.lastMessageAt).getTime() > lastReadMs;
  const pendingAt = c.pendingAction?.createdAt ?? null;
  const hasUnreadPending =
    pendingAt !== null && new Date(pendingAt).getTime() > lastReadMs;
  const isManuallyFlagged = c.flaggedAt !== null;
  return hasUnreadInbound || hasUnreadPending || isManuallyFlagged;
}

function cmpDateDesc(a: string | null, b: string | null): number {
  const av = a ? new Date(a).getTime() : 0;
  const bv = b ? new Date(b).getTime() : 0;
  return bv - av;
}
function cmpDateAsc(a: string | null, b: string | null): number {
  const av = a ? new Date(a).getTime() : Number.MAX_SAFE_INTEGER;
  const bv = b ? new Date(b).getTime() : Number.MAX_SAFE_INTEGER;
  return av - bv;
}

function groupByStage(
  cards: CardView[],
  sort: SortMode
): Record<Stage, CardView[]> {
  const out: Record<Stage, CardView[]> = {
    lead: [], opportunity: [], chase: [], schedule: [], won: [], lost: [],
  };
  for (const c of cards) out[c.stage].push(c);
  for (const s of STAGES) {
    if (s === "won") {
      // WON is a schedule view — primary order is scheduled date+time
      // ascending so today's earliest booking sits at the top, then
      // today's later jobs, then tomorrow, etc. EXCEPTION: any card
      // currently flashing (needs attention — unread inbound,
      // Emma draft pending, or manual flag) floats above the
      // schedule-ordered set so cancellations / day-of changes
      // don't get buried below tomorrow's bookings.
      out[s].sort((a, b) => {
        const aFlash = needsAttention(a);
        const bFlash = needsAttention(b);
        if (aFlash !== bFlash) return aFlash ? -1 : 1;
        const dateCmp = cmpDateAsc(a.scheduleDate, b.scheduleDate);
        if (dateCmp !== 0) return dateCmp;
        // Same-day (or both null): break the tie on start time. Use a
        // sentinel like "99:99" for missing time so unscheduled-time
        // entries fall to the bottom of the date bucket.
        const at = a.scheduleStartTime ?? "99:99";
        const bt = b.scheduleStartTime ?? "99:99";
        return at.localeCompare(bt);
      });
    } else {
      out[s].sort((a, b) => compareCards(a, b, sort));
    }
  }
  return out;
}

function stageFromContainer(id: string | number): Stage | null {
  if (typeof id !== "string") return null;
  if (!id.startsWith("col:")) return null;
  const s = id.slice(4) as Stage;
  return STAGES.includes(s) ? s : null;
}

function filterCards(
  cards: CardView[],
  f: ReturnType<typeof useFilters.getState>,
  currentUserEmail: string
): CardView[] {
  const q = f.q.trim().toLowerCase();
  return cards.filter((c) => {
    if (f.priorityOnly && c.priority !== 1) return false;
    if (f.assignee === "mine" && c.assignedUser !== currentUserEmail) return false;
    if (f.assignee === "unassigned" && c.assignedUser !== null) return false;
    if (
      f.assignee !== "all" &&
      f.assignee !== "mine" &&
      f.assignee !== "unassigned" &&
      c.assignedUser !== f.assignee
    ) {
      return false;
    }
    if (q) {
      const hay = [
        c.contactName,
        c.postcode,
        c.headline,
        c.assignedUser,
        `card#${c.cardId}`,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function App(): JSX.Element {
  const session = useSession();
  if (session.status === "loading") {
    return (
      <div className="flex h-full items-center justify-center text-[12px] text-ink-200">
        loading…
      </div>
    );
  }
  if (session.status === "anonymous") {
    return <Login />;
  }
  return (
    <Router
      currentUser={session.user.email}
      userName={session.user.name ?? session.user.email}
    />
  );
}

function Router({
  currentUser,
  userName,
}: {
  currentUser: string;
  userName: string;
}): JSX.Element {
  const view = useView();
  if (view === "earnings") return <CleanerEarnings userName={userName} />;
  if (view === "growth") return <BusinessGrowth userName={userName} />;
  if (view === "marketing") return <Marketing userName={userName} />;
  if (view === "coverage") return <TechCoverage userName={userName} />;
  if (view === "areas") return <AreaAnalysis userName={userName} />;
  if (view === "db") return <DbBoard userName={userName} />;
  // Pipeline (default)
  return <Board currentUser={currentUser} userName={userName} />;
}

function Board({ currentUser, userName }: { currentUser: string; userName: string }): JSX.Element {
  const qc = useQueryClient();
  useRealtime(qc);
  const filters = useFilters();
  const board = useBoard(filters.includeArchived);
  const patch = usePatchCard();

  // Share the logged-in email with child components that can't easily
  // receive it as a prop (Toolbar, BulkBar, CardActions, etc.). Runs once
  // on mount + whenever the user switches accounts.
  const setCurrentUserStore = useCurrentUserStore((s) => s.set);
  useEffect(() => {
    setCurrentUserStore(currentUser);
  }, [currentUser, setCurrentUserStore]);

  const [activeId, setActiveId] = useState<number | null>(null);
  const [openCardId, setOpenCardId] = useState<number | null>(null);
  const [pendingLost, setPendingLost] = useState<PendingMove | null>(null);
  // Top-bar "New Quote" flow state. newQuoteOpen drives the dedupe
  // search modal; topQuoteVid mounts a standalone QuoteEditor after
  // creation (no slideout context — the board card will catch up via
  // card.updated SSE).
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [topQuoteVid, setTopQuoteVid] = useState<number | null>(null);
  // Account-preview slide-out (View Account button in search popover).
  const [accountPreviewAid, setAccountPreviewAid] = useState<number | null>(
    null
  );
  const searchRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // `/` focuses the search box (unless already in an input).
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== "/") return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const allCards = board.data?.cards ?? [];
  const visibleCards = useMemo(
    () => filterCards(allCards, filters, currentUser),
    [allCards, filters, currentUser]
  );
  const grouped = useMemo(
    () => groupByStage(visibleCards, filters.sort),
    [visibleCards, filters.sort]
  );
  const assignees = useMemo(
    () =>
      Array.from(
        new Set(
          allCards
            .map((c) => c.assignedUser)
            .filter((u): u is string => typeof u === "string" && u.length > 0)
        )
      ).sort(),
    [allCards]
  );

  const activeCard = useMemo(() => {
    if (activeId === null) return null;
    return allCards.find((c) => c.cardId === activeId) ?? null;
  }, [activeId, allCards]);
  const openCard = useMemo(() => {
    if (openCardId === null) return null;
    return allCards.find((c) => c.cardId === openCardId) ?? null;
  }, [openCardId, allCards]);

  const onDragStart = (ev: DragStartEvent): void => {
    setActiveId(Number(ev.active.id));
  };

  const onDragEnd = (ev: DragEndEvent): void => {
    setActiveId(null);
    const { active, over } = ev;
    if (!over) return;

    const cardId = Number(active.id);
    const activeCardNow = allCards.find((c) => c.cardId === cardId);
    if (!activeCardNow) return;

    let targetStage: Stage;
    let targetCards: CardView[];
    let overIndex: number;

    const overStage = stageFromContainer(over.id);
    if (overStage) {
      targetStage = overStage;
      targetCards = grouped[overStage];
      overIndex = targetCards.length;
    } else {
      const overCardId = Number(over.id);
      const owner = allCards.find((c) => c.cardId === overCardId);
      if (!owner) return;
      targetStage = owner.stage;
      targetCards = grouped[targetStage];
      overIndex = targetCards.findIndex((c) => c.cardId === overCardId);
      if (overIndex === -1) overIndex = targetCards.length;
    }

    const filtered = targetCards.filter((c) => c.cardId !== cardId);
    const adjustedIndex = Math.min(overIndex, filtered.length);
    const prev = filtered[adjustedIndex - 1];
    const next = filtered[adjustedIndex];
    const newPosition =
      prev && next
        ? Math.round((prev.position + next.position) / 2)
        : next
          ? next.position - POSITION_GAP
          : prev
            ? prev.position + POSITION_GAP
            : POSITION_GAP;

    if (
      activeCardNow.stage === targetStage &&
      activeCardNow.position === newPosition
    ) {
      return;
    }

    if (targetStage === "lost" && activeCardNow.stage !== "lost") {
      setPendingLost({ cardId, stage: "lost", position: newPosition });
      return;
    }

    patch.mutate({ cardId, stage: targetStage, position: newPosition });
  };

  const confirmLost = (reason: string): void => {
    if (!pendingLost) return;
    patch.mutate(
      { ...pendingLost, closedReason: reason } as {
        cardId: number;
        stage: Stage;
        position: number;
        closedReason?: string;
      }
    );
    setPendingLost(null);
  };

  return (
    <div className="flex h-full flex-col">
      <TopNav
        userName={userName}
        status={
          <>
            {board.isLoading ? <span>loading…</span> : null}
            {board.isError ? (
              <span className="text-slack-pink">error loading board</span>
            ) : null}
          </>
        }
      />

      <Toolbar
        searchRef={searchRef}
        totalVisible={visibleCards.length}
        totalAll={allCards.length}
        assignees={assignees}
        saving={patch.isPending}
        onNewQuote={() => setNewQuoteOpen(true)}
      />

      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <main className="scrollbar-slim flex flex-1 gap-3 overflow-x-auto p-3 md:gap-4 md:p-5">
          {STAGES.map((stage) => (
            <Column
              key={stage}
              stage={stage}
              cards={grouped[stage]}
              onOpenCard={setOpenCardId}
              currentUser={currentUser}
            />
          ))}
        </main>
        <DragOverlay dropAnimation={null}>
          {activeCard ? (
            <div className="w-[18rem]">
              <Card card={activeCard} isOverlay />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <LostReasonModal
        open={pendingLost !== null}
        onCancel={() => setPendingLost(null)}
        onConfirm={confirmLost}
      />

      <Slideout card={openCard} onClose={() => setOpenCardId(null)} />

      <AccountSlideout
        aid={accountPreviewAid}
        onClose={(cardId) => {
          setAccountPreviewAid(null);
          if (cardId !== null) setOpenCardId(cardId);
        }}
      />

      <NewQuoteSearchModal
        open={newQuoteOpen}
        onClose={() => setNewQuoteOpen(false)}
        onOpenQuote={(vid) => setTopQuoteVid(vid)}
        onViewAccount={(aid, cardId) => {
          setNewQuoteOpen(false);
          // If the matched card is currently visible on the board, open
          // its full Slideout. If it's filtered out (archived, off-stage,
          // assigned-to-someone-else under a Mine filter, etc.) the regular
          // Slideout would silently render nothing, so fall back to the
          // AccountSlideout preview keyed by aid.
          if (
            cardId !== null &&
            allCards.some((c) => c.cardId === cardId)
          ) {
            setOpenCardId(cardId);
          } else {
            setAccountPreviewAid(aid);
          }
        }}
      />

      {topQuoteVid !== null ? (
        <QuoteEditor
          vid={topQuoteVid}
          onClose={() => setTopQuoteVid(null)}
        />
      ) : null}

      <BulkBar />
    </div>
  );
}
